import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Download, BarChart3, FileText } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

export default function Reports() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns`);
      setCampaigns(res.data);
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const downloadReport = (id) => {
    window.open(`${API}/campaigns/${id}/report`, "_blank");
    toast.success("Descargando reporte...");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">Reportes</h1>
        <p className="text-sm text-zinc-500 mt-1">Descarga reportes detallados de cada campana</p>
      </div>

      <div className="bg-zinc-900 border border-white/5 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-900/50 text-xs uppercase tracking-wider text-zinc-500 font-bold border-b border-white/5">
              <th className="py-3 px-4 text-left">Campana</th>
              <th className="py-3 px-4 text-center">Estado</th>
              <th className="py-3 px-4 text-center">Total</th>
              <th className="py-3 px-4 text-center">Enviados</th>
              <th className="py-3 px-4 text-center">Fallidos</th>
              <th className="py-3 px-4 text-center">Tasa Exito</th>
              <th className="py-3 px-4 text-right">Descargar</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, i) => {
              const successRate = c.stats?.total > 0
                ? Math.round((c.stats.sent / c.stats.total) * 100) : 0;
              return (
                <tr key={c.id} data-testid={`report-row-${i}`} className="hover:bg-white/[0.02] transition-colors duration-200 border-b border-white/5 last:border-0">
                  <td className="py-3 px-4">
                    <p className="text-sm text-zinc-200 font-medium">{c.name}</p>
                    <p className="text-[10px] font-mono text-zinc-600 mt-0.5">
                      {new Date(c.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                      c.status === 'completed' ? 'bg-blue-500/15 text-blue-400 border-blue-500/20' :
                      c.status === 'active' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' :
                      'bg-zinc-500/15 text-zinc-400 border-zinc-500/20'
                    }`}>
                      {c.status === 'completed' ? 'Completado' : c.status === 'active' ? 'Activo' : c.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-mono text-zinc-300">{c.stats?.total || 0}</td>
                  <td className="py-3 px-4 text-center text-sm font-mono text-emerald-400">{c.stats?.sent || 0}</td>
                  <td className="py-3 px-4 text-center text-sm font-mono text-red-400">{c.stats?.failed || 0}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-sm font-mono ${successRate >= 90 ? 'text-emerald-400' : successRate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {successRate}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      data-testid={`download-report-${i}`}
                      onClick={() => downloadReport(c.id)}
                      className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 rounded-md px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors duration-200"
                    >
                      <Download className="w-3 h-3" /> CSV
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {campaigns.length === 0 && (
          <div className="text-center py-12">
            <BarChart3 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No hay campanas para generar reportes</p>
          </div>
        )}
      </div>
    </div>
  );
}
